import React, { useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Slider from "@mui/material/Slider";
import Box from "@mui/material/Box";
import Cropper from "react-easy-crop";

const getCroppedBlob = (src, crop, mimeType = "image/jpeg", quality = 0.92) =>
  new Promise((resolve, reject) => {
    if (!src || !crop) return reject(new Error("Missing source or crop area."));
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const targetWidth = 1200;
      const targetHeight = 480;

      const canvas = document.createElement("canvas");
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas context not available"));

      ctx.drawImage(
        img,
        Math.max(0, Math.round(crop.x)),
        Math.max(0, Math.round(crop.y)),
        Math.max(1, Math.round(crop.width)),
        Math.max(1, Math.round(crop.height)),
        0,
        0,
        targetWidth,
        targetHeight
      );

      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error("Canvas is empty"))),
        mimeType,
        quality
      );
    };
    img.onerror = () => reject(new Error("Failed to load image"));
    img.src = src;
  });

const HandleCropPromoBanner = ({
  open,
  imageSrc,
  onCancel,
  onSave,
  outputType = "image/jpeg",
  outputQuality = 0.92,
  zoomRange = [1, 3],
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [saving, setSaving] = useState(false);

  const onCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      setSaving(true);
      const blob = await getCroppedBlob(
        imageSrc,
        croppedAreaPixels,
        outputType,
        outputQuality
      );
      onSave(blob);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
      <DialogTitle>Crop Banner</DialogTitle>
      <DialogContent>
        <Box sx={{ position: "relative", width: "100%", height: 420 }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            aspect={1200 / 480} 
          />
        </Box>
        <Slider
          min={zoomRange[0]}
          max={zoomRange[1]}
          step={0.1}
          value={zoom}
          onChange={(_, v) => setZoom(v)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HandleCropPromoBanner;
